import { getWebOutbox } from "@client/iphone/lib/videoSync/createVideoSync";
import {
  createProgressSync,
  type ProgressFlushResult,
  type ProgressPayload,
} from "@client/iphone/lib/videoSync/progressSync";
import { type StorageLike, type SyncFeature } from "@client/sync";
import { type ProgressRow } from "@lib/videoStateTypes";

export type { ProgressFlushResult, ProgressPayload };

/**
 * The funder-workspace player's watch-progress writes go through this durable
 * outbox (queue + retrying worker), NOT a fire-and-forget mutation: progress
 * accrued offline must survive until reconnect — and, on the iPad native app,
 * survive a force-quit — exactly like the queue player's own progress path.
 *
 * Storage mirrors createVideoSync's split: native persists to localStorage so
 * the queue outlives an app restart; web uses the module-level in-memory
 * per-user outbox (retries within the page session, never persists across a
 * reload, never flushes under another user). The storage KEY is per-user on
 * native too, so a queue written by one signed-in user can never flush under
 * the next user of a shared device — a stale user's entries just sit inert.
 *
 * The key is also distinct from the mobile store's progress queue
 * ("mobileVideoSync.progress.v1"): two workers over one queue double-send.
 */
export function createVideoStatesProgressSync(args: {
  userId: string;
  isNative: boolean;
  onSuccess: (result: ProgressFlushResult, payload: ProgressPayload) => void;
  // Commit gate — the provider passes "not impersonating" so an in-flight
  // send that raced an identity flip is never treated as delivered.
  shouldCommit?: () => boolean;
  // Test seams; production uses the real defaults.
  storage?: StorageLike;
  isOnline?: () => boolean;
}): SyncFeature<ProgressPayload> {
  return createProgressSync({
    onSuccess: args.onSuccess,
    storageKey: `videoStatesSync.progress.v1.${args.userId}`,
    storage:
      args.storage ?? (args.isNative ? undefined : getWebOutbox(args.userId)),
    isOnline: args.isOnline,
    shouldCommit: args.shouldCommit,
  });
}

/**
 * Pure reconciliation of one flushed progress result into the
 * getUserVideoState cache value. Extracted from the provider so the ordering
 * rules stay unit-testable.
 *
 * ONE RESPONSE, TWO FACTS, TWO CLOCKS.
 *
 * A response says where the playhead is AND whether the video is finished, and
 * those are not ordered by the same thing — so each is merged on its own clock
 * and neither decides for the other.
 *
 * POSITION is ordered by `clientUpdatedAt`. A rejection (`applied: false` —
 * impersonation no-op, or the server's LWW refusing a stale write) means the
 * server holds position truth we don't, so its row wins outright; an applied
 * write wins only if it postdates the cached row, so an out-of-order response
 * can't regress a newer optimistic one.
 *
 * COMPLETION is ordered by `completionUpdatedAt`, the clock of the funder's
 * last MANUAL verdict. Deriving it from position produced a bug in every
 * direction it was tried: letting position speak for the row reverted a tick;
 * refusing the server's completion killed the AUTOMATIC LATCH (it arrives
 * inside an ordinary position response, and it is the feature's main path);
 * and taking whichever side had a completion could never honour a CLEAR — after
 * "Mark as not done", any flush still carrying the old `completedAt` re-ticked
 * the step. With a clock there is nothing to infer: the cache keeps its
 * completion only while it holds a verdict the server has not seen yet, and
 * otherwise the server's answer — latch included — is taken whole.
 */
export function applyProgressFlushResult<T extends { progress: ProgressRow[] }>(
  prev: T | undefined,
  result: ProgressFlushResult,
): T | undefined {
  if (prev == null) {
    return prev;
  }

  const incoming = result.current;
  const idx = prev.progress.findIndex((r) => r.videoId === incoming.videoId);

  if (idx === -1) {
    return { ...prev, progress: [...prev.progress, incoming] };
  }

  const cached = prev.progress[idx];
  const merged = mergeProgressRow(cached, incoming, result.applied);

  if (merged === cached) {
    return prev;
  }

  return {
    ...prev,
    progress: prev.progress.map((row, i) => (i === idx ? merged : row)),
  };
}

function mergeProgressRow(
  cached: ProgressRow,
  incoming: ProgressRow,
  applied: boolean,
): ProgressRow {
  const takePosition =
    !applied ||
    new Date(incoming.clientUpdatedAt).getTime() >=
      new Date(cached.clientUpdatedAt).getTime();

  // The cache wins only while it holds a manual verdict the server's row
  // predates — i.e. one still in flight. Equal clocks are the same verdict read
  // back, and a null incoming clock alongside a completion is the automatic
  // latch, which is the server's to make.
  const pendingVerdict =
    cached.completionUpdatedAt != null &&
    (incoming.completionUpdatedAt == null ||
      new Date(incoming.completionUpdatedAt).getTime() <
        new Date(cached.completionUpdatedAt).getTime());

  if (takePosition && !pendingVerdict) {
    return incoming;
  }

  // Nothing to take: identity is preserved so the caller can leave the cache
  // untouched rather than re-render every consumer of an equal row.
  if (
    !takePosition &&
    (pendingVerdict ||
      (cached.completedAt === incoming.completedAt &&
        cached.completionUpdatedAt === incoming.completionUpdatedAt))
  ) {
    return cached;
  }

  return takePosition
    ? {
        ...incoming,
        completedAt: cached.completedAt,
        completionUpdatedAt: cached.completionUpdatedAt,
      }
    : {
        ...cached,
        completedAt: incoming.completedAt,
        completionUpdatedAt: incoming.completionUpdatedAt,
      };
}

/**
 * True when an `applied: false` flush result is just the server no-op'ing a
 * DUPLICATE of a write it already holds — the commit guard deliberately
 * leaves a sent head queued when its worker stopped mid-send, so the
 * successor re-sends it and the strict-LWW same-timestamp check rejects it
 * with `current` equal to what we sent. That rejection carries no new
 * information and must NOT trigger the cross-round invalidate (a refetch
 * storm that can clobber other videos' optimistic rows). A genuine
 * cross-device rejection returns a DIFFERENT row (newer clientUpdatedAt).
 * Timestamps compare by epoch value, not string identity — the server's
 * serialization of the stored instant need not byte-match our ISO input.
 */
export function isDuplicateNoOpRejection(
  payload: ProgressPayload,
  result: ProgressFlushResult,
): boolean {
  return (
    !result.applied &&
    result.current.videoId === payload.videoId &&
    // The playhead must match too: a same-millisecond write from another
    // device with a DIFFERENT position is a genuine conflict, not our echo.
    // A true duplicate echoes our stored value exactly (doubles round-trip
    // losslessly through JSON), so strict equality is safe here.
    result.current.watchedSeconds === payload.watchedSeconds &&
    new Date(result.current.clientUpdatedAt).getTime() ===
      new Date(payload.clientUpdatedAt).getTime()
  );
}
